import { configurationService, DropdownOption } from '@/services/configurationService';
import { ExtendedCreateProjectData, ProjectSchedule, ScheduleMilestone } from '@/types';
import { getCountryCodes } from '@/utils/phoneValidation';
import { Calendar, Edit, Globe, MapPin, Plus, Save, Trash2 } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker, Dropdown, Input, PhoneInput, TextArea } from '../forms';

interface ScheduleStepProps {
  data: Partial<ExtendedCreateProjectData>;
  updateData: (data: Partial<ExtendedCreateProjectData>) => void;
  setValid: (isValid: boolean) => void;
}

interface ConfigurationData {
  countries: DropdownOption[];
  states: DropdownOption[];
  timezones: DropdownOption[];
  regions: DropdownOption[];
  languages: DropdownOption[];
  currencies: DropdownOption[];
}

const ScheduleStep: React.FC<ScheduleStepProps> = ({ data, updateData, setValid }) => {
  const { t } = useTranslation();
  const countryCodes = getCountryCodes();

  // State management
  const [configurations, setConfigurations] = useState<ConfigurationData>({
    countries: [],
    states: [],
    timezones: [],
    regions: [],
    languages: [],
    currencies: [],
  });
  const [isLoadingConfigs, setIsLoadingConfigs] = useState(true);
  const [editingSchedule, setEditingSchedule] = useState<number | null>(null);
  const [editingMilestone, setEditingMilestone] = useState<{
    scheduleIndex: number;
    milestoneIndex: number;
  } | null>(null);

  // Load configurations on mount
  useEffect(() => {
    const loadConfigurations = async () => {
      setIsLoadingConfigs(true);
      try {
        const dropdowns = await configurationService.getMultipleDropdowns([
          'countries',
          'states',
          'timezones',
          'regions',
          'languages',
          'currencies',
        ]);

        setConfigurations({
          countries: dropdowns.countries || [],
          states: dropdowns.states || [],
          timezones: dropdowns.timezones || [],
          regions: dropdowns.regions || [],
          languages: dropdowns.languages || [],
          currencies: dropdowns.currencies || [],
        });
      } catch (error) {
        console.error('Failed to load configurations:', error);
      } finally {
        setIsLoadingConfigs(false);
      }
    };

    loadConfigurations();
  }, []);

  // Convert configurations to dropdown options
  const dropdownOptions = useMemo(
    () => ({
      countries: configurations.countries.map(opt => ({ value: opt.value, label: opt.label })),
      states: configurations.states.map(opt => ({ value: opt.value, label: opt.label })),
      timezones: configurations.timezones.map(opt => ({ value: opt.value, label: opt.label })),
      regions: configurations.regions.map(opt => ({ value: opt.value, label: opt.label })),
      languages: configurations.languages.map(opt => ({ value: opt.value, label: opt.label })),
      currencies: configurations.currencies.map(opt => ({ value: opt.value, label: opt.label })),
    }),
    [configurations]
  );

  // Validation
  useEffect(() => {
    const hasLocation = !!data.location?.address?.trim();
    setValid(!!hasLocation);
  }, [data.location]); // Removed setValid from dependencies

  // Location update handlers
  const updateLocation = useCallback(
    (locationData: Partial<typeof data.location>) => {
      updateData({
        location: {
          ...data.location,
          ...locationData,
        },
      });
    },
    [data.location, updateData]
  );

  const updateAdvanced = useCallback(
    (advancedData: Partial<typeof data.advanced>) => {
      updateData({
        advanced: {
          ...data.advanced,
          ...advancedData,
        },
      });
    },
    [data.advanced, updateData]
  );

  // Schedule management
  const addSchedule = useCallback(() => {
    const newSchedule: ProjectSchedule = {
      id: `schedule-${Date.now()}`,
      name: '',
      startDate: new Date(),
      endDate: new Date(),
      milestones: [],
      dependencies: [],
    };

    const schedules = data.schedules || [];
    updateData({
      schedules: [...schedules, newSchedule],
    });
    setEditingSchedule(schedules.length);
  }, [data.schedules, updateData]);

  const updateSchedule = useCallback(
    (index: number, scheduleData: Partial<ProjectSchedule>) => {
      const schedules = [...(data.schedules || [])];
      schedules[index] = { ...schedules[index], ...scheduleData };
      updateData({ schedules });
    },
    [data.schedules, updateData]
  );

  const removeSchedule = useCallback(
    (index: number) => {
      const schedules = [...(data.schedules || [])];
      schedules.splice(index, 1);
      updateData({ schedules });
      setEditingSchedule(null);
    },
    [data.schedules, updateData]
  );

  // Milestone management
  const addMilestone = useCallback(
    (scheduleIndex: number) => {
      const newMilestone: ScheduleMilestone = {
        id: `milestone-${Date.now()}`,
        name: '',
        date: new Date(),
        description: '',
        status: 'pending',
      };

      const schedules = [...(data.schedules || [])];
      if (schedules[scheduleIndex]) {
        schedules[scheduleIndex].milestones = [
          ...schedules[scheduleIndex].milestones,
          newMilestone,
        ];
        updateData({ schedules });
      }
    },
    [data.schedules, updateData]
  );

  const updateMilestone = useCallback(
    (scheduleIndex: number, milestoneIndex: number, milestoneData: Partial<ScheduleMilestone>) => {
      const schedules = [...(data.schedules || [])];
      if (schedules[scheduleIndex] && schedules[scheduleIndex].milestones[milestoneIndex]) {
        schedules[scheduleIndex].milestones[milestoneIndex] = {
          ...schedules[scheduleIndex].milestones[milestoneIndex],
          ...milestoneData,
        };
        updateData({ schedules });
      }
    },
    [data.schedules, updateData]
  );

  const removeMilestone = useCallback(
    (scheduleIndex: number, milestoneIndex: number) => {
      const schedules = [...(data.schedules || [])];
      if (schedules[scheduleIndex]) {
        schedules[scheduleIndex].milestones.splice(milestoneIndex, 1);
        updateData({ schedules });
      }
    },
    [data.schedules, updateData]
  );

  return (
    <div className="space-y-6 font-['Inter',sans-serif]">
      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
        `}
      </style>

      {/* Project Location */}
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <MapPin className="h-5 w-5 text-[var(--brand-primary)]" />
          <h4 className="text-lg font-bold text-[#1F1F1F] leading-[1.4em]">
            {t('projects.creation.projectLocation.title')}
          </h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Dropdown
            label={t('projects.creation.projectLocation.country')}
            options={dropdownOptions.countries}
            value={data.location?.country || ''}
            onChange={value => updateLocation({ country: value })}
            required
            disabled={isLoadingConfigs}
          />

          <Dropdown
            label={t('projects.creation.projectLocation.timezone')}
            options={dropdownOptions.timezones}
            value={data.location?.timezone || ''}
            onChange={value => updateLocation({ timezone: value })}
            required
            disabled={isLoadingConfigs}
          />

          <Input
            label={t('projects.creation.projectLocation.address')}
            value={data.location?.address || ''}
            onChange={e => updateLocation({ address: e.target.value })}
            placeholder={t('projects.creation.projectLocation.placeholders.address')}
            required
          />

          <Input
            label={t('projects.creation.projectLocation.city')}
            value={data.location?.city || ''}
            onChange={e => updateLocation({ city: e.target.value })}
            placeholder={t('projects.creation.projectLocation.placeholders.city')}
            required
          />

          <Dropdown
            label={t('projects.creation.projectLocation.state')}
            options={dropdownOptions.states}
            value={data.location?.state || ''}
            onChange={value => updateLocation({ state: value })}
            placeholder={t('common.select')}
            disabled={isLoadingConfigs}
          />

          <Input
            label={t('projects.creation.projectLocation.zipCode')}
            value={data.location?.zipCode || ''}
            onChange={e => updateLocation({ zipCode: e.target.value })}
            placeholder={t('projects.creation.projectLocation.placeholders.zipCode')}
            required
          />

          <PhoneInput
            label={t('projects.creation.projectLocation.phone')}
            countryCodes={countryCodes}
            selectedCountryCode={data.location?.countryCode || '+1'}
            value={data.location?.phone || ''}
            onCountryCodeChange={code => updateLocation({ countryCode: code })}
            onChange={value => updateLocation({ phone: value })}
            placeholder={t('projects.creation.projectLocation.placeholders.phone')}
          />
        </div>
      </div>

      {/* Advanced Settings */}
      <div className="border border-gray-200 rounded-lg p-4">
        <div className="flex items-center space-x-2 mb-4">
          <Globe className="h-5 w-5 text-red-600" />
          <h4 className="text-lg font-medium text-gray-900">
            {t('projects.creation.advancedSettings.title')}
          </h4>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Dropdown
            label={t('projects.creation.advancedSettings.region')}
            options={dropdownOptions.regions}
            value={data.advanced?.region || ''}
            onChange={value => updateAdvanced({ region: value })}
            required
            disabled={isLoadingConfigs}
          />

          <Dropdown
            label={t('projects.creation.advancedSettings.language')}
            options={dropdownOptions.languages}
            value={data.advanced?.language || ''}
            onChange={value => updateAdvanced({ language: value })}
            required
            disabled={isLoadingConfigs}
          />

          <Dropdown
            label={t('projects.budget')}
            options={dropdownOptions.currencies}
            value={data.advanced?.currency || 'USD'}
            onChange={value => updateAdvanced({ currency: value })}
            required
            disabled={isLoadingConfigs}
          />

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="testProject"
              checked={data.advanced?.isTestProject || false}
              onChange={e => updateAdvanced({ isTestProject: e.target.checked })}
              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
            />
            <label htmlFor="testProject" className="text-sm font-medium text-gray-700">
              {t('projects.creation.advancedSettings.testProject')}
            </label>
          </div>
        </div>
      </div>

      {/* Project Schedules */}
      <div className="border border-gray-200 rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <Calendar className="h-5 w-5 text-[var(--brand-primary)]" />
            <div>
              <h4 className="text-lg font-bold text-[#1F1F1F] leading-[1.4em]">
                {t('projects.creation.schedule.title')}
              </h4>
              <p className="text-sm text-[#6E7077] font-normal leading-[1.75em]">
                {t('projects.creation.schedule.description')}
              </p>
            </div>
          </div>
          <button
            onClick={addSchedule}
            className="inline-flex items-center px-4 py-2 text-sm font-bold text-white bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] rounded-lg transition-colors uppercase tracking-wide leading-[1.2102272851126534em]"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('projects.creation.schedule.addSchedule')}
          </button>
        </div>

        {!data.schedules || data.schedules.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="mx-auto h-12 w-12 text-[#6E7077]" />
            <h3 className="mt-4 text-lg font-bold text-[#1F1F1F] leading-[1.4em]">
              {t('projects.creation.schedule.noSchedulesYet')}
            </h3>
            <p className="mt-2 text-sm text-[#6E7077] font-normal leading-[1.75em]">
              {t('projects.creation.schedule.noSchedulesDescription')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {data.schedules.map((schedule, scheduleIndex) => (
              <div key={schedule.id} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-4">
                  <h5 className="text-base font-bold text-[#1F1F1F] leading-[1.4em]">
                    {schedule.name || t('projects.creation.schedule.scheduleName')}
                  </h5>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        setEditingSchedule(editingSchedule === scheduleIndex ? null : scheduleIndex)
                      }
                      className="p-2 text-[#6E7077] hover:text-[var(--brand-primary)] hover:bg-[var(--brand-primary-light)] rounded-lg transition-colors"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => removeSchedule(scheduleIndex)}
                      className="p-2 text-[#6E7077] hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {editingSchedule === scheduleIndex ? (
                  <div className="space-y-4">
                    <Input
                      label={t('projects.creation.schedule.scheduleName')}
                      value={schedule.name}
                      onChange={e => updateSchedule(scheduleIndex, { name: e.target.value })}
                      placeholder={t('projects.creation.schedule.scheduleName')}
                    />

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <DatePicker
                        label={t('projects.creation.schedule.scheduleStartDate')}
                        value={schedule.startDate.toISOString().split('T')[0]}
                        onChange={date =>
                          updateSchedule(scheduleIndex, { startDate: new Date(date || schedule.startDate) })
                        }
                      />
                      <DatePicker
                        label={t('projects.creation.schedule.scheduleEndDate')}
                        value={schedule.endDate.toISOString().split('T')[0]}
                        onChange={date =>
                          updateSchedule(scheduleIndex, { endDate: new Date(date || schedule.endDate) })
                        }
                        minDate={schedule.startDate.toISOString().split('T')[0]}
                      />
                    </div>

                    <div className="flex justify-end">
                      <button
                        onClick={() => setEditingSchedule(null)}
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-[#1F1F1F] bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors leading-[1.75em]"
                      >
                        <Save className="h-4 w-4 mr-2" />
                        {t('common.save')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-[#6E7077] font-normal leading-[1.75em]">
                      {schedule.startDate.toLocaleDateString()} -{' '}
                      {schedule.endDate.toLocaleDateString()}
                    </p>
                  </div>
                )}

                {/* Milestones */}
                <div className="mt-6">
                  <div className="flex items-center justify-between mb-4">
                    <h6 className="text-sm font-bold text-[#1F1F1F] leading-[1.4em]">
                      {t('projects.creation.schedule.milestones')}
                    </h6>
                    <button
                      onClick={() => addMilestone(scheduleIndex)}
                      className="inline-flex items-center px-3 py-1 text-xs font-medium text-[var(--brand-primary)] bg-[var(--brand-primary-light)] hover:bg-[#FFEAB8] rounded-lg transition-colors leading-[1.75em]"
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      {t('projects.creation.schedule.addMilestone')}
                    </button>
                  </div>

                  {schedule.milestones.length === 0 ? (
                    <p className="text-sm text-[#6E7077] italic font-normal leading-[1.75em]">
                      {t('projects.creation.schedule.noMilestonesYet', 'No milestones added')}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {schedule.milestones.map((milestone, milestoneIndex) => (
                        <div
                          key={milestone.id}
                          className="bg-white border border-gray-200 rounded-lg p-3"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1">
                              {editingMilestone?.scheduleIndex === scheduleIndex &&
                              editingMilestone?.milestoneIndex === milestoneIndex ? (
                                <div className="space-y-3">
                                  <Input
                                    label={t('projects.creation.schedule.milestoneName')}
                                    value={milestone.name}
                                    onChange={e =>
                                      updateMilestone(scheduleIndex, milestoneIndex, {
                                        name: e.target.value,
                                      })
                                    }
                                  />
                                  <DatePicker
                                    label={t('projects.creation.schedule.milestoneDate')}
                                    value={milestone.date.toISOString().split('T')[0]}
                                    onChange={date =>
                                      updateMilestone(scheduleIndex, milestoneIndex, {
                                        date: new Date(date || milestone.date),
                                      })
                                    }
                                  />
                                  <TextArea
                                    label={t('projects.creation.schedule.milestoneDescription')}
                                    value={milestone.description}
                                    onChange={value =>
                                      updateMilestone(scheduleIndex, milestoneIndex, {
                                        description: value,
                                      })
                                    }
                                    rows={2}
                                  />
                                  <Dropdown
                                    label={t('projects.creation.schedule.milestoneStatus')}
                                    options={[
                                      {
                                        value: 'pending',
                                        label: t(
                                          'projects.creation.schedule.milestoneStatuses.pending'
                                        ),
                                      },
                                      {
                                        value: 'completed',
                                        label: t(
                                          'projects.creation.schedule.milestoneStatuses.completed'
                                        ),
                                      },
                                      {
                                        value: 'delayed',
                                        label: t(
                                          'projects.creation.schedule.milestoneStatuses.delayed'
                                        ),
                                      },
                                    ]}
                                    value={milestone.status}
                                    onChange={(value: any) =>
                                      updateMilestone(scheduleIndex, milestoneIndex, {
                                        status: value,
                                      })
                                    }
                                  />
                                  <div className="flex justify-end space-x-2">
                                    <button
                                      onClick={() => setEditingMilestone(null)}
                                      className="px-3 py-1 text-xs font-medium text-[#1F1F1F] bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                                    >
                                      {t('common.save')}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <h7 className="text-sm font-medium text-[#1F1F1F]">
                                    {milestone.name}
                                  </h7>
                                  <p className="text-xs text-[#6E7077]">
                                    {milestone.date.toLocaleDateString()}
                                  </p>
                                  {milestone.description && (
                                    <p className="text-xs text-[#6E7077] mt-1">
                                      {milestone.description}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="flex items-center space-x-2 ml-4">
                              <button
                                onClick={() =>
                                  setEditingMilestone({ scheduleIndex, milestoneIndex })
                                }
                                className="p-1 text-[#6E7077] hover:text-[var(--brand-primary)] rounded transition-colors"
                              >
                                <Edit className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() => removeMilestone(scheduleIndex, milestoneIndex)}
                                className="p-1 text-[#6E7077] hover:text-red-600 rounded transition-colors"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleStep;
